import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  FormControl,
  FormHelperText,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import API from "../utils/api";
import { toast } from "react-toastify";
import {
  buildAssignedRepOptions,
  formatAssignedRepOptionLabel,
} from "../utils/assignedRepOptions";
import StateSelect from "./StateSelect";
import { formatPhone } from "../utils/formatPhone";

const emptyForm = {
  first_name: "",
  last_name: "",
  address: "",
  city: "",
  state: "",
  zip_code: "",
  phone: "",
  email: "",
  assigned_rep: "",
};

function territoryList(territories) {
  return Array.isArray(territories) ? territories : [];
}

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    bgcolor: "background.paper",
  },
  "& .MuiOutlinedInput-input": {
    py: 1.35,
  },
  "& .MuiOutlinedInput-input::placeholder": {
    color: "text.secondary",
    opacity: 1,
  },
};

function FieldLabel({ children, required = false }) {
  return (
    <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, mb: 0.75, color: "text.primary" }}>
      {children}
      {required ? " *" : ""}
    </Typography>
  );
}

function firstTerritoryForRep(rep, territories) {
  const options = territoryList(territories);
  const repIds = new Set(
    (Array.isArray(rep?.territories) ? rep.territories : [])
      .map((id) => Number(id))
      .filter((id) => Number.isFinite(id))
  );
  const match = options.find((territory) => repIds.has(Number(territory.id)));
  return match?.id ?? null;
}

export default function AddSearchLeadDialog({
  open,
  onClose,
  role,
  territories,
  users,
  onCreated,
}) {
  const isSalesRep = role === "Sales Rep";
  const [form, setForm] = useState(emptyForm);
  const [assignedRepError, setAssignedRepError] = useState(false);
  const [saving, setSaving] = useState(false);

  const repOptions = buildAssignedRepOptions({ users });

  const reset = () => {
    setForm(emptyForm);
    setAssignedRepError(false);
    setSaving(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const setField = (field) => (event) => {
    const value = event.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field === "assigned_rep" && value) setAssignedRepError(false);
  };

  const handleSave = async () => {
    const address = form.address.trim();
    const city = form.city.trim();
    const state = form.state.trim();
    if (!address || !city || !state) {
      toast.warning("Street, city, and state are required.");
      return;
    }

    const selectedRep = isSalesRep
      ? null
      : repOptions.find((rep) => String(rep.id) === String(form.assigned_rep));
    if (!isSalesRep && !selectedRep) {
      setAssignedRepError(true);
      return;
    }

    const territoryId = isSalesRep
      ? territoryList(territories)[0]?.id ?? null
      : firstTerritoryForRep(selectedRep, territories);
    if (!territoryId) {
      toast.error(
        isSalesRep
          ? "You need a territory assigned before you can add a lead."
          : "That rep has no territory you can add a lead to."
      );
      return;
    }

    const payload = {
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      address,
      city,
      state,
      zip_code: form.zip_code.trim(),
      phone: formatPhone(form.phone) || null,
      email: form.email.trim() || null,
      status: "New",
      territory: territoryId,
    };
    if (!isSalesRep) payload.assigned_rep = selectedRep.id;

    try {
      setSaving(true);
      const response = await API.post("/leads/", payload);
      toast.success("Lead added successfully!");
      reset();
      onClose();
      onCreated(response.data);
    } catch (err) {
      console.error("Error saving lead:", err);
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to add lead");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
        },
      }}
    >
      <Box sx={{ px: 3, pt: 2.5, pb: 2, display: "flex", alignItems: "flex-start", gap: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: "0.72rem",
              fontWeight: 700,
              letterSpacing: "0.08em",
              color: "text.secondary",
              mb: 0.75,
            }}
          >
            NEW LEAD
          </Typography>
          <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, lineHeight: 1.2 }}>
            Add a lead
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Create the lead and open it to start a proposal.
          </Typography>
        </Box>
        <IconButton
          aria-label="Close"
          onClick={handleClose}
          disabled={saving}
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            width: 36,
            height: 36,
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box sx={{ px: 3, pb: 2.5 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ order: 1 }}>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>First name</FieldLabel>
              <TextField
                placeholder="First name"
                value={form.first_name}
                onChange={setField("first_name")}
                fullWidth
                autoFocus
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Last name</FieldLabel>
              <TextField
                placeholder="Last name"
                value={form.last_name}
                onChange={setField("last_name")}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          </Stack>

          <Box sx={{ order: { xs: 3, sm: 2 } }}>
            <FieldLabel required>Address</FieldLabel>
            <TextField
              placeholder="Street address"
              value={form.address}
              onChange={setField("address")}
              fullWidth
              sx={fieldSx}
            />
          </Box>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ order: { xs: 4, sm: 3 } }}>
            <Box sx={{ flex: 1.4 }}>
              <FieldLabel required>City</FieldLabel>
              <TextField
                placeholder="City"
                value={form.city}
                onChange={setField("city")}
                fullWidth
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ flex: 0.8 }}>
              <FieldLabel required>State</FieldLabel>
              <StateSelect
                placeholder="State"
                value={form.state}
                onChange={setField("state")}
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ flex: 0.8 }}>
              <FieldLabel>ZIP</FieldLabel>
              <TextField
                placeholder="ZIP"
                value={form.zip_code}
                onChange={setField("zip_code")}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          </Stack>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ order: { xs: 2, sm: 4 } }}>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Phone</FieldLabel>
              <TextField
                placeholder="(xxx) xxx-xxxx"
                value={formatPhone(form.phone)}
                onChange={(event) =>
                  setForm((prev) => ({ ...prev, phone: formatPhone(event.target.value) }))
                }
                fullWidth
                sx={fieldSx}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <FieldLabel>Email</FieldLabel>
              <TextField
                placeholder="Email"
                type="email"
                value={form.email}
                onChange={setField("email")}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          </Stack>

          {!isSalesRep && (
            <Box sx={{ order: 5 }}>
              <FieldLabel required>Assigned rep</FieldLabel>
              <FormControl fullWidth error={assignedRepError}>
                <Select
                  displayEmpty
                  value={form.assigned_rep}
                  onChange={setField("assigned_rep")}
                  sx={{
                    borderRadius: 2.5,
                    "& .MuiSelect-select": { py: 1.35 },
                  }}
                >
                  <MenuItem value="">
                    <Typography color="text.secondary">Select a rep</Typography>
                  </MenuItem>
                  {repOptions.map((rep) => (
                    <MenuItem key={rep.id} value={String(rep.id)}>
                      {formatAssignedRepOptionLabel(rep)}
                    </MenuItem>
                  ))}
                </Select>
                {assignedRepError && (
                  <FormHelperText>Lead must have an assigned rep</FormHelperText>
                )}
              </FormControl>
            </Box>
          )}
        </Stack>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderTop: "1px solid",
          borderColor: "divider",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <Button
          variant="outlined"
          onClick={handleClose}
          disabled={saving}
          sx={{ textTransform: "none", borderRadius: 2, px: 2, fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          sx={{ textTransform: "none", borderRadius: 2, px: 2.25, fontWeight: 700 }}
        >
          {saving ? "Saving..." : "Create lead"}
        </Button>
      </Box>
    </Dialog>
  );
}
